import { FormEvent } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PURCHASING_PATHS, tPurchasing } from "@/features/staff/purchasing/purchasing"

type Props = {
  onDone?: () => void
}

/**
 * Adding a supplier.
 *
 * An explicit submit, unlike the fields on the cards behind it: a supplier with
 * no name is not a half-written supplier, it is nothing, and the create endpoint
 * is the one place that distinction has to be made. Once the row exists every
 * field on it is light and operational, so editing commits inline and this form
 * never reappears.
 *
 * Contact and phone are optional on purpose — `Location::Supplier` validates only
 * `name`. A restaurant that knows a supplier as "el señor de las tortillas" and
 * nothing else can still record a delivery from them.
 */
export function SupplierForm({ onDone }: Props) {
  const { data, setData, post, errors, processing } = useForm({
    name: "",
    contact_name: "",
    phone: "",
  })

  const complete = data.name.trim().length > 0

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!complete || processing) return

    post(PURCHASING_PATHS.createSupplier, {
      preserveScroll: true,
      onSuccess: () => onDone?.(),
    })
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="supplier-new-form">
      <div className="space-y-2">
        <Label htmlFor="supplier-new-name">{tPurchasing("supplier_name_label")}</Label>
        <Input
          id="supplier-new-name"
          value={data.name}
          onChange={(event) => setData("name", event.target.value)}
          placeholder={tPurchasing("supplier_name_placeholder")}
          data-testid="supplier-new-name"
          required
        />
        <FieldError error={errors.name} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="supplier-new-contact">{tPurchasing("supplier_contact_label")}</Label>
        <Input
          id="supplier-new-contact"
          value={data.contact_name}
          onChange={(event) => setData("contact_name", event.target.value)}
          placeholder={tPurchasing("supplier_contact_placeholder")}
          data-testid="supplier-new-contact"
        />
        <FieldError error={errors.contact_name} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="supplier-new-phone">{tPurchasing("supplier_phone_label")}</Label>
        <Input
          id="supplier-new-phone"
          type="tel"
          inputMode="tel"
          value={data.phone}
          onChange={(event) => setData("phone", event.target.value)}
          placeholder={tPurchasing("supplier_phone_placeholder")}
          data-testid="supplier-new-phone"
        />
        <FieldError error={errors.phone} />
      </div>

      <div className="flex items-center justify-end gap-2">
        {onDone && (
          <Button type="button" variant="outline" onClick={onDone} data-testid="supplier-new-cancel">
            {tPurchasing("cancel")}
          </Button>
        )}
        <Button type="submit" disabled={processing || !complete} data-testid="supplier-new-submit">
          {tPurchasing("supplier_create")}
        </Button>
      </div>
    </form>
  )
}
